import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import { Alert, Container } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CssBaseline from "@mui/material/CssBaseline"
import Grid from "@mui/material/Grid"
import { createTheme } from "@mui/material/styles"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useFormik } from "formik"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import authService from "services/authService"
import type { IChangeForgotPassword } from "types/userTypes"
import * as Yup from "yup"

const _theme = createTheme()
interface IChangeForgotPasswordValues {
  password: string
  confirmPassword: string
  email: string
  code: string
}

export function ChangePassword() {
  const navigate = useNavigate()
  const [error, setError] = useState<string>()
  const [_errorVisible, setErrorVisible] = useState<boolean>()
  const search = useLocation().search

  // biome-ignore lint/correctness/useExhaustiveDependencies: formik is stable and doesn't need to be in deps
  useEffect(() => {
    const codeParam = new URLSearchParams(search).get("code") || ""
    const emailParam = new URLSearchParams(search).get("email") || ""
    formik.setFieldValue("email", emailParam)
    formik.setFieldValue("code", codeParam)
  }, [search])

  const initialValues: IChangeForgotPasswordValues = {
    password: "",
    confirmPassword: "",
    email: "",
    code: "",
  }

  const changePasswordValidationScheme = Yup.object({
    email: Yup.string().required("Please enter email"),
    code: Yup.string().required("Please enter confirmation code"),
    password: Yup.string()
      .required("Please enter valid password.")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
        "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character",
      ),
    confirmPassword: Yup.string()
      .required("Required")
      .test("password-match", "Password musth match", function (value) {
        return this.parent.password === value
      }),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: changePasswordValidationScheme,
    onSubmit: (values) => {
      const changeUserPassword: IChangeForgotPassword = {
        password: values.password,
        code: values.code,
        username: values.email,
      }

      authService
        .changeForgotPassword(changeUserPassword)
        .then((_status) => {
          authService
            .logout()
            .then(() => {
              navigate("/login")
            })
            .catch(() => {
              navigate("/login")
            })
        })
        .catch((err) => {
          setError(err.message)
          setErrorVisible(true)
        })
    },
  })

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" paddingBottom={4}>
          Change password
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoComplete="confirmation-code"
                name="code"
                required
                fullWidth
                id="code"
                label="Verification code"
                value={formik.values.code}
                onChange={formik.handleChange}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={formik.touched.code && formik.errors.code}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoComplete="email"
                name="email"
                required
                fullWidth
                id="email"
                label="Email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="password"
                label="New password"
                type="password"
                id="password"
                autoComplete="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm new password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
            </Grid>
          </Grid>
          {error !== undefined && <Alert severity="error">{error}</Alert>}
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
            Send
          </Button>
        </form>
      </Box>
    </Container>
  )
}
